import { Component, OnInit, HostListener } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { tap, take } from 'rxjs/operators';

import { IAuroraState } from '@apps/aurora.state';
import * as fromAuthStore from '@apps/auth/store';
import { getCounterTwoFactorTries } from '@apps/auth/store/auth.reducer';

@Component({
    selector: 'aa-two-factor-login',
    templateUrl: './two-factor-login.component.html',
    styleUrls: [
        './two-factor-login.component.scss'
    ],
    standalone: false
})
export class TwoFactorLoginComponent implements OnInit {
    public remainingTries: Observable<number>;
    public readonly CodeField = 'code';
    formGroup: UntypedFormGroup;
    idToken: string;
    public verifyCodeLabelKey: 'AUTH.VERIFYCODE.LABEL' | 'AUTH.VERIFYCODE.LABELMAIL' = 'AUTH.VERIFYCODE.LABEL';

    constructor(
        private route: ActivatedRoute,
        private store: Store<IAuroraState>,
        private formBuilder: UntypedFormBuilder
    ) {
        this.route.paramMap
            .pipe(
                take(1),
                tap((params: ParamMap): void => {
                    this.idToken = params.get('idtoken');
                    const channel = params.get('channel'); // 'mail' | 'totp'

                    this.verifyCodeLabelKey = channel === 'mail'
                        ? 'AUTH.VERIFYCODE.LABELMAIL'
                        : 'AUTH.VERIFYCODE.LABEL';
                })
            )
            .subscribe();
    }

    ngOnInit() {
        this.formGroup = this.formBuilder.group({
            [this.CodeField]: ['', Validators.required]
        });

        this.remainingTries = this.store.select(getCounterTwoFactorTries);
    }

    @HostListener('keydown.enter', ['$event'])
    onEnterKeyDown(event: KeyboardEvent): void {
        event.preventDefault();
        this.onSubmit();
    }

    public showCodeRequiredError(): boolean {
        const ctl = this.formGroup?.get(this.CodeField);
        return !!ctl && ctl.hasError('required') && (ctl.dirty || ctl.touched);
    }

    onSubmit = () => {
        if (this.formGroup.invalid) {
            // mark all invalid controls so errors display immediately
            Object.values(this.formGroup.controls).forEach(c => {
                c.markAsDirty();
                c.markAsTouched();
                c.updateValueAndValidity({ onlySelf: true });
            });

            // focus the digit-code inputs if present
            const el = document.querySelector('aa-input-digit-code input.digit') as HTMLElement | null;
            el?.focus();

            return;
        }

        this.store.dispatch(fromAuthStore.AuthLoginStoreActions.AuthTwoFactorLoginRequested({
            idToken: this.idToken,
            code: this.formGroup.get(this.CodeField).value
        }));
    };
}
